import { supabase } from '../../config/supabaseClient';
import { ENV } from '../../config/env';

// =====================================================
// TYPES & INTERFACES
// =====================================================

export interface Project {
  id: string;
  organization_id: string;
  name: string;
  description?: string;
  status: 'planning' | 'active' | 'on-hold' | 'completed' | 'archived';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  owner_id: string;
  start_date?: string;
  due_date?: string;
  estimated_hours: number;
  actual_hours: number;
  completion_percentage: number;
  budget: number;
  actual_cost: number;
  tags: string[];
  custom_fields: Record<string, any>;
  color: string;
  created_at: string;
  updated_at: string;
}

export interface ProjectInput {
  organization_id: string;
  name: string;
  description?: string;
  status?: 'planning' | 'active' | 'on-hold' | 'completed' | 'archived';
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  owner_id: string;
  start_date?: string;
  due_date?: string;
  estimated_hours?: number;
  budget?: number;
  tags?: string[];
  custom_fields?: Record<string, any>;
  color?: string;
}

export interface ListProjectsParams {
  organization_id: string;
  status?: string;
  owner_id?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

// =====================================================
// HELPER: Get Auth Token
// =====================================================

async function getToken(): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');
  return session.access_token;
}

// =====================================================
// API BASE URL
// =====================================================

const API_BASE_URL = `${ENV.API_URL}/api/project-management/projects`;

// =====================================================
// PROJECTS SERVICE
// =====================================================

export const projectsService = {
  /**
   * List projects with optional filters
   */
  async list(params: ListProjectsParams): Promise<{ data: Project[]; error: any }> {
    try {
      const token = await getToken();
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, String(value));
        }
      });

      const response = await fetch(`${API_BASE_URL}?${queryParams.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const error = await response.json();
        return { data: [], error: error.error || 'Failed to fetch projects' };
      }

      const result = await response.json();
      return { data: result.data || [], error: null };
    } catch (err: any) {
      console.error('Error in projectsService.list:', err);
      return { data: [], error: err.message || 'Network error' };
    }
  },

  /**
   * Get project by ID with statistics
   */
  async getById(projectId: string, organizationId: string): Promise<{ data: any; error: any }> {
    try {
      const token = await getToken();
      const queryParams = new URLSearchParams({ organization_id: organizationId });

      const response = await fetch(`${API_BASE_URL}/${projectId}?${queryParams.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const error = await response.json();
        return { data: null, error: error.error || 'Failed to fetch project' };
      }

      const result = await response.json();
      return { data: result.data, error: null };
    } catch (err: any) {
      console.error('Error in projectsService.getById:', err);
      return { data: null, error: err.message || 'Network error' };
    }
  },

  /**
   * Create a new project
   */
  async create(input: ProjectInput): Promise<{ data: Project | null; error: any }> {
    try {
      const token = await getToken();

      const response = await fetch(API_BASE_URL, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(input),
      });

      if (!response.ok) {
        const error = await response.json();
        return { data: null, error: error.error || 'Failed to create project' };
      }

      const result = await response.json();
      return { data: result.data, error: null };
    } catch (err: any) {
      console.error('Error in projectsService.create:', err);
      return { data: null, error: err.message || 'Network error' };
    }
  },

  /**
   * Update a project
   */
  async update(
    projectId: string,
    organizationId: string,
    updates: Partial<ProjectInput>
  ): Promise<{ data: Project | null; error: any }> {
    try {
      const token = await getToken();
      const queryParams = new URLSearchParams({ organization_id: organizationId });

      const response = await fetch(`${API_BASE_URL}/${projectId}?${queryParams.toString()}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        const error = await response.json();
        return { data: null, error: error.error || 'Failed to update project' };
      }

      const result = await response.json();
      return { data: result.data, error: null };
    } catch (err: any) {
      console.error('Error in projectsService.update:', err);
      return { data: null, error: err.message || 'Network error' };
    }
  },

  /**
   * Delete a project
   */
  async delete(projectId: string, organizationId: string): Promise<{ error: any }> {
    try {
      const token = await getToken();
      const queryParams = new URLSearchParams({ organization_id: organizationId });

      const response = await fetch(`${API_BASE_URL}/${projectId}?${queryParams.toString()}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const error = await response.json();
        return { error: error.error || 'Failed to delete project' };
      }

      return { error: null };
    } catch (err: any) {
      console.error('Error in projectsService.delete:', err);
      return { error: err.message || 'Network error' };
    }
  },

  /**
   * Get organization project statistics
   */
  async getStats(organizationId: string): Promise<{ data: any; error: any }> {
    try {
      const token = await getToken();
      const queryParams = new URLSearchParams({ organization_id: organizationId });

      const response = await fetch(`${API_BASE_URL}/stats?${queryParams.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const error = await response.json();
        return { data: null, error: error.error || 'Failed to fetch stats' };
      }

      const result = await response.json();
      return { data: result.data, error: null };
    } catch (err: any) {
      console.error('Error in projectsService.getStats:', err);
      return { data: null, error: err.message || 'Network error' };
    }
  },
};

export default projectsService;
